import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import { appApi, useGetAssetsQuery, useGetEquipmentProjectsQuery } from "../../../store/appApi";
import { selectActiveProjectId } from "../../../store/projectSlice";
import { selectUserRole } from "../../../store/authSlice";
import {
  bulkUploadAssetsApi,
  deleteAssetApi,
  downloadAssetsExcel,
  downloadAssetTemplate,
} from "../../../services/assets";
import { ASSET_CONDITIONS } from "./AddAsset";

const inr = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN")}`;

const AssetList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activeProjectId = useSelector(selectActiveProjectId);
  const isSuperAdmin = useSelector(selectUserRole) === "super-admin";
  const { data: projData } = useGetEquipmentProjectsQuery();
  const projects = projData?.projects || [];

  // "" = the active project; "all" = every project (super-admin).
  const [projectId, setProjectId] = useState("");
  const [search, setSearch] = useState("");
  const [condition, setCondition] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const scope = projectId || activeProjectId || "";
  const { data, isFetching } = useGetAssetsQuery(
    { projectId: scope, search, condition },
    { skip: !scope },
  );
  const items = data?.items || [];

  // Project-wise grouping (matters when "All projects" is selected).
  const groups = items.reduce((acc, a) => {
    const name = a.projectId?.projectName || "Unassigned";
    (acc[name] = acc[name] || []).push(a);
    return acc;
  }, {});

  const download = async () => {
    setDownloading(true);
    try {
      await downloadAssetsExcel({ projectId: scope, search, condition });
    } catch {
      Swal.fire("Error", "Could not download the Excel file.", "error");
    } finally {
      setDownloading(false);
    }
  };

  // Bulk-add from an Excel file into the project currently selected above
  // (not "All projects" — a file goes into one project).
  const bulkUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!scope || scope === "all") {
      Swal.fire("Select a project", "Pick one project (not All) before uploading.", "warning");
      return;
    }
    setUploading(true);
    try {
      const res = await bulkUploadAssetsApi(file, scope);
      dispatch(appApi.util.invalidateTags(["Assets"]));
      const skipped = res.data?.skipped || [];
      Swal.fire({
        icon: "success",
        title: `${res.data?.created ?? 0} assets added`,
        html: skipped.length
          ? `<div style="text-align:left;max-height:200px;overflow:auto;font-size:13px">${skipped
              .map((s) => `Row ${s.row}: ${s.reason}`)
              .join("<br/>")}</div>`
          : undefined,
      });
    } catch (err) {
      Swal.fire("Upload failed", err?.response?.data?.message || "Could not read the file.", "error");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (a) => {
    const ok = await Swal.fire({
      icon: "warning",
      title: "Delete asset?",
      text: a.assetName,
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#dc3545",
    });
    if (!ok.isConfirmed) return;
    try {
      await deleteAssetApi(a._id);
      dispatch(appApi.util.invalidateTags(["Assets"]));
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.message || "Could not delete.", "error");
    }
  };

  return (
    <>
      <PageTitle activeMenu="Asset List" motherMenu="Assets" />
      <div className="card">
        <div className="card-header d-flex flex-wrap gap-2 align-items-center justify-content-between">
          <h4 className="card-title mb-0">
            Assets{" "}
            <small className="text-muted">
              {items.length} items · {inr(data?.totalValue)}
            </small>
          </h4>
          <div className="d-flex flex-wrap gap-2">
            <select
              className="form-control form-control-sm"
              style={{ width: 200 }}
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
            >
              <option value="">Active project</option>
              {isSuperAdmin && <option value="all">All projects</option>}
              {projects.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.projectName}
                </option>
              ))}
            </select>
            <select
              className="form-control form-control-sm"
              style={{ width: 130 }}
              value={condition}
              onChange={(e) => setCondition(e.target.value)}
            >
              <option value="">Any condition</option>
              {ASSET_CONDITIONS.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
            <input
              className="form-control form-control-sm"
              style={{ width: 180 }}
              placeholder="Search name / code / location"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button className="btn btn-outline-primary btn-sm" disabled={downloading || !scope} onClick={download}>
              {downloading ? "Preparing…" : "Download Excel"}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: "none" }}
              onChange={bulkUpload}
            />
            <button
              className="btn btn-outline-secondary btn-sm"
              disabled={uploading || !scope || scope === "all"}
              title="Upload an Excel file into the selected project"
              onClick={() => fileRef.current?.click()}
            >
              {uploading ? "Uploading…" : "Bulk upload"}
            </button>
            <button className="btn btn-link btn-sm" onClick={downloadAssetTemplate}>
              Template
            </button>
            <Link to="/assets/add" className="btn btn-primary btn-sm">
              + Add Asset
            </Link>
          </div>
        </div>
        <div className="card-body">
          {!scope ? (
            <div className="text-center text-muted py-4">Select a project first.</div>
          ) : isFetching ? (
            <div className="text-center py-4">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-center text-muted py-4">No assets found.</div>
          ) : (
            Object.entries(groups).map(([name, rows]) => (
              <div key={name} className="mb-4">
                <h5 className="mb-2">{name}</h5>
                <div className="table-responsive">
                  <table className="table table-bordered table-hover verticle-middle">
                    <thead className="thead-dark">
                      <tr>
                        <th>Asset</th>
                        <th>Category</th>
                        <th>Code</th>
                        <th>Qty</th>
                        <th>Unit cost</th>
                        <th>Value</th>
                        <th>Purchased</th>
                        <th>Location</th>
                        <th>Assigned to</th>
                        <th>Condition</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((a) => (
                        <tr key={a._id}>
                          <td className="fw-bold">{a.assetName}</td>
                          <td>{a.category || "—"}</td>
                          <td>{a.assetCode || "—"}</td>
                          <td>{a.quantity}</td>
                          <td>{inr(a.unitCost)}</td>
                          <td>{inr((a.quantity || 0) * (a.unitCost || 0))}</td>
                          <td>{a.purchaseDate ? moment(a.purchaseDate).format("DD MMM YYYY") : "—"}</td>
                          <td>{a.location || "—"}</td>
                          <td>{a.assignedTo || "—"}</td>
                          <td>{a.condition}</td>
                          <td className="text-nowrap">
                            <button
                              className="btn btn-xs btn-primary me-1"
                              onClick={() => navigate(`/assets/edit/${a._id}`, { state: { asset: a } })}
                            >
                              Edit
                            </button>
                            {isSuperAdmin && (
                              <button className="btn btn-xs btn-danger" onClick={() => remove(a)}>
                                Delete
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default AssetList;
